import { useState, useCallback, useEffect } from 'react';
import { Location } from '../models';
import { apiClient } from '../services/ApiClient';
import { locationCache } from '../services/LocationCacheService';

/**
 * Hook for managing location data
 * Handles location selection and location details
 * Integrates with LocationCacheService for client-side caching
 */
export function useLocationData() {
  // State for the selected location ID
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  
  // State for location details
  const [locationDetails, setLocationDetails] = useState<{
    name: string;
    region: string;
    country: string;
    coordinates: { latitude: number; longitude: number };
  }>({ 
    name: "", 
    region: "", 
    country: "", 
    coordinates: { latitude: 0, longitude: 0 } 
  });

  /**
   * Fetch location details if needed
   * Uses the location cache first, falls back to API
   */
  const fetchLocationDetails = useCallback(async (locationId: number) => {
    console.log(`Fetching details for location ${locationId}`);
    
    try {
      // Try to get from cache first
      if (locationCache.has(locationId)) {
        const cachedLocation = locationCache.get(locationId);
        console.log('Found location in cache', cachedLocation);
        return cachedLocation;
      }
      
      // If not in cache, fetch from API
      console.log(`Location ${locationId} not in cache, fetching from API`);
      const location = await apiClient.getLocationById(locationId);
      
      // Add to cache for future use
      locationCache.set(location);
      
      return location;
    } catch (error) {
      console.error(`Error fetching location ${locationId}:`, error);
      return null;
    }
  }, []);

  /**
   * Handle location selection
   * Sets the selected location ID and updates location details
   * Also stores the location in cache if it's not null
   */
  const handleLocationSelect = useCallback((location: Location | null) => {
    const locationId = location ? location.id : null;
    setSelectedLocationId(locationId);

    if (location) {
      // Add to cache before using
      locationCache.set(location);
      
      // Set details from location
      setLocationDetails({
        name: location.name || "",
        region: (location as any).region || "Unknown Region", 
        country: (location as any).country || "Unknown Country", 
        coordinates: location.coordinates || { latitude: 0, longitude: 0 }
      });
    } else {
      // Reset details if location is null
      setLocationDetails({
        name: "",
        region: "", 
        country: "", 
        coordinates: { latitude: 0, longitude: 0 } 
      });
    }
  }, []);

  /**
   * Resolves a locationId to location name
   * Uses cache if available, falls back to default
   */
  const getLocationName = useCallback((locationId: number, defaultName?: string) => {
    return locationCache.getLocationName(locationId, defaultName);
  }, []);

  /**
   * Resolves a locationId to a full Location object
   * Returns from cache if available, otherwise fetches from API
   */
  const resolveLocation = useCallback(async (locationId: number): Promise<Location | null> => {
    return locationCache.fetchIfNeeded(locationId);
  }, []);

  return {
    selectedLocationId,
    locationDetails,
    setSelectedLocationId,
    setLocationDetails,
    handleLocationSelect,
    fetchLocationDetails,
    getLocationName,
    resolveLocation,
    locationCache // Expose the cache for direct access if needed
  };
}
